import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { opTime } from 'utils/dates';
import { getOpParams } from 'utils/getOpParams';

// Visitor software license ids (SW-VISIT-SS, SW-VISIT-MS, SW-VISIT-LS).
const VISITOR_SOFTWARE_LICENSE_IDS = [137, 138, 139];

/**
 * Determines if the banner should show based on the org being in an active visitor trial and gathers information needed to show the banner.
 * @returns information required to render the banner and whether the banner should be shown.
 */
export const useTrialBanner = () => {
  const { orgId } = getOpParams();

  const { data: visitorSoftwareLicense, isLoading: isLoadingSoftwareLicense } =
    useOpQuery({
      apiEndpointName: 'listOrgSoftwareLicenses',
      parameters: [
        orgId,
        {
          filter: `softwareLicenseId:(${VISITOR_SOFTWARE_LICENSE_IDS.join(',')})`,
        },
      ],
      select: (heliumResponse) => (heliumResponse.json.data ?? [])[0],
      suppressErrorMessage: true,
    });

  const { activatedAt, softwareLicenseOffer } = visitorSoftwareLicense ?? {};

  const hasVisitorTrial = softwareLicenseOffer?.type === 'trial';

  const billingStartsAt = opTime.utc(activatedAt).startOf('day'); // Keep the activatedAt date as is - not changed by timezone
  const now = opTime.utc().startOf('day');

  const daysLeft = billingStartsAt.diff(now, 'day');

  const bannerData = {
    billingStartsAt,
    daysLeft,
    isLoading: isLoadingSoftwareLicense,
  };

  /**
   * Don't show while the software license is loading or
   * when the org has no active visitor trial
   */
  if (!hasVisitorTrial || isLoadingSoftwareLicense) {
    return { ...bannerData, showBanner: false };
  }

  return { ...bannerData, showBanner: true };
};
